import React from "react";
import { tenantDisplayName } from "../../../utils/tenantDisplayName";
import { inputClass, labelClass } from "./tenantDetailStyles";

/**
 * Cluster A / A1 — pick an EXISTING tenant as a co-tenant (no new tenant row).
 *
 * Purely presentational: the container (AdminTenantDetailPage) owns the search
 * query state, fires `fetchAdminTenants({ q, limit })`, and passes results +
 * selection handlers down. This component never calls the API itself, so it
 * stays trivially renderable in isolation and keeps the dedup decision in one
 * place (the submit handler reads `selectedTenant`, never createAdminTenant).
 */
export default function TenantParticipantPicker({
  query,
  onQueryChange,
  results,
  loading,
  error,
  selectedTenant,
  onSelect,
  onClear,
  disabled,
}) {
  if (selectedTenant) {
    const name =
      tenantDisplayName(selectedTenant) ||
      selectedTenant.name ||
      String(selectedTenant.id || "");
    const sub = [selectedTenant.email, selectedTenant.phone]
      .map((x) => String(x || "").trim())
      .filter(Boolean)
      .join(" · ");
    return (
      <div
        data-testid="participant-picker-selected"
        className="flex items-center justify-between gap-2 rounded-[8px] border border-emerald-500/30 bg-emerald-500/[0.06] px-3 py-2"
      >
        <div className="min-w-0">
          <div className="truncate text-[13px] font-semibold text-[#0f172a] dark:text-[#eef2ff]">
            {name}
          </div>
          {sub ? (
            <div className="truncate text-[11px] text-[#64748b] dark:text-[#6b7a9a]">
              {sub}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onClear}
          disabled={disabled}
          className="shrink-0 text-[12px] font-semibold text-[#64748b] underline dark:text-[#8090b0]"
          style={{ cursor: disabled ? "default" : "pointer" }}
        >
          Ändern
        </button>
      </div>
    );
  }

  const rows = Array.isArray(results) ? results : [];
  const q = String(query || "").trim();

  return (
    <div>
      <label htmlFor="assign-second-existing-search" className={labelClass}>
        Bestehenden Mieter suchen (Name, E-Mail, Telefon)
      </label>
      <input
        id="assign-second-existing-search"
        type="text"
        className={inputClass}
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        disabled={disabled}
        autoComplete="off"
        placeholder="Suchbegriff eingeben …"
      />
      {error ? (
        <p className="mt-1 text-[12px] text-[#f87171]">{error}</p>
      ) : null}
      <div className="mt-2">
        {loading ? (
          <p className="m-0 text-[12px] text-[#64748b] dark:text-[#6b7a9a]">Suche läuft …</p>
        ) : q.length < 2 ? (
          <p className="m-0 text-[12px] text-[#64748b] dark:text-[#6b7a9a]">
            Mindestens 2 Zeichen eingeben.
          </p>
        ) : rows.length === 0 ? (
          <p className="m-0 text-[12px] text-[#64748b] dark:text-[#6b7a9a]">
            Keine Treffer.
          </p>
        ) : (
          <ul className="m-0 max-h-[220px] list-none overflow-y-auto rounded-[8px] border border-black/10 p-0 dark:border-white/[0.07]">
            {rows.map((t) => {
              const name = tenantDisplayName(t) || t.name || String(t.id || "");
              const sub = [t.email, t.phone]
                .map((x) => String(x || "").trim())
                .filter(Boolean)
                .join(" · ");
              return (
                <li key={String(t.id)}>
                  <button
                    type="button"
                    onClick={() => onSelect(t)}
                    disabled={disabled}
                    className="block w-full border-b border-black/[0.06] px-3 py-2 text-left last:border-b-0 hover:bg-slate-100 dark:border-white/[0.05] dark:hover:bg-white/[0.04]"
                    style={{ cursor: disabled ? "default" : "pointer" }}
                  >
                    <div className="truncate text-[13px] font-semibold text-[#0f172a] dark:text-[#eef2ff]">
                      {name}
                    </div>
                    {sub ? (
                      <div className="truncate text-[11px] text-[#64748b] dark:text-[#6b7a9a]">
                        {sub}
                      </div>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
